/**
 * The five layout shells.
 *
 * Every route names one of these and no route composes its own chrome.
 * `focus` is a mode on the member shell rather than a shell of its own,
 * because a wizard keeps the session, the notifications and the market clock
 * alive; it only puts the navigation away.
 */

import { Suspense } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import {
  MATCHING_WINDOW_END,
  MATCHING_WINDOW_START,
  ROUTES,
  formatJalaliDate,
  toJalali,
  toPersianDigits,
} from '@metal/shared'
import {
  AppBar,
  AppShell,
  Brand,
  Button,
  Icon,
  IconButton,
  MarketTicker,
  Nav,
  SessionClock,
  Skeleton,
  Topbar,
  type NavItemSpec,
} from '@metal/ui'
import { useMarketClock, useSession } from '@metal/ui/session'
import { useTicker } from '@/lib/queries'
import { useTradeSide, type TradeSide } from '@/lib/trade-side'
import { AccountMenu } from './AccountMenu'
import { useUnreadNotifications } from './AccountRail'
import { useAuthModal } from './AppRoot'
import { MobileTabBar } from './MobileTabBar'

/**
 * The member rows. Every member trades both ways, so their own listings sit
 * behind one row that opens the side they are working on (see trade-side);
 * the list page switches between عرضه‌ها and تقاضاها itself. Six rows, not
 * seven: the topbar has room for six down to 1024.
 */
function memberNav(side: TradeSide): NavItemSpec[] {
  return [
    { to: ROUTES.board(), label: 'تابلو', icon: 'chart', match: ['/board*', '/offers/1*', '/rfqs/2*'] },
    {
      to: side === 'sell' ? ROUTES.myOffers() : ROUTES.myRfqs(),
      label: 'عرضه و تقاضا',
      icon: side === 'sell' ? 'cathode' : 'list',
      match: ['/offers/mine*', '/rfqs/mine*', '/offers/new*', '/rfqs/new*'],
    },
    { to: ROUTES.bids(), label: 'پیشنهادهای من', icon: 'exchange' },
    { to: ROUTES.deals(), label: 'معاملات', icon: 'doc', match: ['/deals*', '/negotiations*'] },
    { to: ROUTES.marketPrices(), label: 'بازار', icon: 'percent', match: ['/market*'] },
    { to: ROUTES.wallet(), label: 'کیف پول', icon: 'wallet', match: ['/wallet*'] },
  ]
}

/** The line under the brand, on every shell. */
const BRAND_SUB = 'بازار معاملات مس'

/** The brand as the copyright line names it; the same name the Brand mark prints. */
const BRAND_FA = 'فیدار تک'

/** The trading window, read from the same constants the market clock runs on. */
const TRADING_HOURS_FA = `ساعت معاملات ${toPersianDigits(MATCHING_WINDOW_START)} تا ${toPersianDigits(MATCHING_WINDOW_END)}`

const PUBLIC_NAV: NavItemSpec[] = [
  // The board is shown to a visitor but gated: the 'authenticated' guard on
  // /board sends anyone not signed in to the login page.
  { to: ROUTES.board(), label: 'تابلوی معاملات', icon: 'chart', match: ['/board*'] },
  { to: ROUTES.news(), label: 'اخبار', icon: 'doc', match: ['/news*'] },
  { to: ROUTES.ads(), label: 'آگهی‌ها', icon: 'grid' },
  { to: ROUTES.links(), label: 'لینک‌های مفید', icon: 'external' },
]

/**
 * The four operator edited pages, in footer order.
 *
 * The slugs are the ones the platform seeds. The body of each page is content
 * the operator writes; only the label in the footer is fixed here.
 */
const CONTENT_PAGES: Array<{ slug: string; labelFa: string }> = [
  { slug: 'trading-guide', labelFa: 'راهنمای معامله' },
  { slug: 'terms', labelFa: 'قوانین و مقررات' },
  { slug: 'about', labelFa: 'درباره ما' },
  { slug: 'contact', labelFa: 'تماس با ما' },
]

/** The page skeleton while a route's code loads. */
function Loading() {
  return (
    <div className="page">
      <Skeleton h={28} w="220px" />
      <Skeleton h={180} />
      <Skeleton h={320} />
    </div>
  )
}

/**
 * The visitor's shell.
 *
 * No market strip and no market rows in the navigation: every rate, volume
 * and board on this side of the gate is only for a signed in member. What is
 * left in the open is the news, the ads, the links and the content pages.
 */
export function PublicLayout() {
  const clock = useMarketClock()
  const { user } = useSession()
  const auth = useAuthModal()

  // Signed in, the public bar carries the account itself, not a bare «ورود» to
  // it: the name and the menu that reaches the dashboard and the account
  // areas, exactly as the member bar does. Signed out, one button
  // opens the modal over the page: signing in and signing up are one act.
  const entry = user ? (
    <AccountMenu />
  ) : (
    <Button variant="primary" size="sm" onClick={auth.open}>
      ورود یا ثبت‌نام
    </Button>
  )

  return (
    <AppShell
      topbar={
        <Topbar variant="public">
          <Brand sub={BRAND_SUB} />
          <div className="sep" />
          <Nav items={PUBLIC_NAV} />
          <div className="grow" />
          <SessionClock open={clock.open} closesAt={clock.closesAt} opensAt={clock.opensAt} short />
          <div className="sep" />
          {entry}
        </Topbar>
      }
      appbar={
        <AppBar>
          <Brand sub={BRAND_SUB} />
          <div className="grow" />
          {entry}
        </AppBar>
      }
      ticker={
        <nav className="subnav" aria-label="منوی اصلی">
          {PUBLIC_NAV.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              className={({ isActive }) => (isActive ? 'is-on' : undefined)}
            >
              <Icon name={it.icon} />
              {it.label}
            </NavLink>
          ))}
        </nav>
      }
    >
      <Suspense fallback={<Loading />}>
        <Outlet />
      </Suspense>
      <PublicFooter />
    </AppShell>
  )
}

/**
 * The public footer: who runs the market, when it trades, the four content
 * pages and the copyright line. A phone, an address or a licence number
 * appears here only once the operator has a real one to show.
 */
function PublicFooter() {
  const year = toPersianDigits(toJalali(new Date()).jy)
  return (
    <footer className="no-print" style={{ borderBlockStart: '1px solid var(--c-border)', marginBlockStart: 'var(--s-9)' }}>
      <div className="page col" style={{ paddingBlock: 'var(--s-7)', gap: 'var(--s-5)' }}>
        <div className="row-b wrap" style={{ gap: 'var(--s-6)', alignItems: 'flex-start' }}>
          <div className="col" style={{ gap: 'var(--s-3)' }}>
            <Brand sub={BRAND_SUB} />
            <span className="t-sm t-sec">{TRADING_HOURS_FA}</span>
          </div>
          <nav className="row wrap" aria-label="لینک‌های پایین صفحه" style={{ gap: 'var(--s-5)' }}>
            {CONTENT_PAGES.map((p) => (
              <NavLink key={p.slug} className="t-sm t-sec" to={ROUTES.contentPage(p.slug)}>
                {p.labelFa}
              </NavLink>
            ))}
          </nav>
        </div>
        <p className="t-cap t-muted" style={{ margin: 0 }}>
          © {year} {BRAND_FA}. همه حقوق محفوظ است.
        </p>
      </div>
    </footer>
  )
}

/** Sign in and the complete-profile flow. Brand only, no navigation. */
export function AuthLayout() {
  const { user, signOut } = useSession()
  return (
    <div className="app">
      <header className="topbar">
        <Brand sub={BRAND_SUB} />
        <div className="grow" />
        {user && (
          <Button variant="ghost" size="sm" icon="logout" onClick={() => void signOut()}>
            خروج
          </Button>
        )}
      </header>
      <main className="main" id="main">
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  )
}

export function MemberLayout({ mode = 'default' }: { mode?: 'default' | 'focus' }) {
  const clock = useMarketClock()
  const { user, loading } = useSession()
  const location = useLocation()
  const ticker = useTicker()
  const unread = useUnreadNotifications() ?? 0
  // The pip is drawn for the eye only, so the bell's name carries the count.
  const bellLabel = unread > 0 ? `${toPersianDigits(unread)} اعلان خوانده‌نشده` : 'اعلان‌ها'
  const [tradeSide] = useTradeSide(user?.sides ?? [])

  // The market strip is part of the member chrome, on every page of it, so
  // moving between pages never makes it come and go.
  const showTicker = mode === 'default'

  if (loading) return <Loading />

  // A visitor on a member route: a shared link, a bookmark, or a session that
  // ended. Waiting is only right while the session is still being read; after
  // that nobody is coming, and holding the skeleton hides the sign in page
  // behind it forever. The guard inside the outlet is what sends them there,
  // carrying this path to come back to, so the shell steps aside for it.
  if (!user) {
    return (
      <Suspense fallback={<Loading />}>
        <Outlet />
      </Suspense>
    )
  }

  return (
    <AppShell
      topbar={
        <Topbar variant="app">
          <Brand sub={BRAND_SUB} to={ROUTES.dashboard()} />
          <div className="sep" />
          {mode === 'focus' ? (
            <FocusBar />
          ) : (
            <>
              <Nav items={memberNav(tradeSide)} />
              <div className="grow" />
              <SessionClock open={clock.open} closesAt={clock.closesAt} opensAt={clock.opensAt} />
              <div className="sep" />
              <div className="row" style={{ gap: 4 }}>
                <IconButton
                  icon="bell"
                  label={bellLabel}
                  size="sm"
                  pip={unread > 0}
                  to={ROUTES.notifications()}
                />
                <div className="sep" />
                <AccountMenu />
              </div>
            </>
          )}
        </Topbar>
      }
      appbar={
        <AppBar>
          {mode === 'focus' ? (
            <>
              <span className="t-sm t-sb" style={{ minInlineSize: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {location.pathname.startsWith('/offers') ? 'ثبت عرضه جدید' : 'ثبت تقاضای خرید'}
              </span>
              <div className="grow" />
              <Button
                variant="ghost"
                size="sm"
                icon="x"
                to={location.pathname.startsWith('/offers') ? ROUTES.myOffers() : ROUTES.myRfqs()}
              >
                انصراف
              </Button>
            </>
          ) : (
            <>
              <Brand sub={BRAND_SUB} to={ROUTES.dashboard()} />
              <div className="grow" />
              <SessionClock
                open={clock.open}
                closesAt={clock.closesAt}
                opensAt={clock.opensAt}
                short
              />
              <IconButton
                icon="bell"
                label={bellLabel}
                size="sm"
                pip={unread > 0}
                to={ROUTES.notifications()}
              />
              <AccountMenu compact />
            </>
          )}
        </AppBar>
      }
      ticker={
        showTicker && ticker.data ? (
          <MarketTicker items={ticker.data.items} stampFa={marketDayFa(ticker.data.market?.referenceDate)} />
        ) : undefined
      }
      bottomBar={mode === 'default' ? <MobileTabBar /> : undefined}
    >
      <Suspense fallback={<Loading />}>
        <Outlet />
      </Suspense>
    </AppShell>
  )
}

/**
 * The strip's one dateline: the market day its figures belong to. That is the
 * latest snapshot day, which is not always yesterday (a holiday moves it), so
 * the strip names the date rather than saying «روز گذشته».
 */
function marketDayFa(referenceDate: string | undefined): string | undefined {
  return referenceDate ? `اطلاعات روز ${formatJalaliDate(referenceDate)}` : undefined
}

/**
 * The wizard bar. The navigation rows and the ticker are replaced by the
 * wizard name, the account, the autosave stamp and a way out, because a form
 * you can wander away from mid-step is a form with a half-written draft.
 */
function FocusBar() {
  const { user } = useSession()
  const location = useLocation()
  const isOffer = location.pathname.startsWith('/offers')
  return (
    <>
      <span className="t-sm t-sb">{isOffer ? 'ثبت عرضه جدید' : 'ثبت تقاضای خرید'}</span>
      <div className="grow" />
      <span className="t-cap t-muted">{user?.shortName}</span>
      <div className="sep" />
      <Button
        variant="ghost"
        size="sm"
        icon="x"
        to={isOffer ? ROUTES.myOffers() : ROUTES.myRfqs()}
      >
        انصراف
      </Button>
    </>
  )
}

/**
 * A page that carries its own chrome.
 *
 * Template B of the landing pair is the only route on it: a page meant to be
 * nearly empty cannot be judged with a navigation bar sitting on top of it,
 * and the pair is there to be judged.
 */
export function BareLayout() {
  return (
    <div className="app">
      <main className="main" id="main">
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  )
}

/** The notice and the deal documents print on A4. No application chrome. */
export function PrintLayout() {
  return (
    <div className="app">
      <main className="main" id="main">
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  )
}
